import React from "react";
import { X, RotateCcw } from "lucide-react";
import { SaddleCloth } from "./SaddleCloth";

// Per-ticket strikes keyed "slot:post" — ticket-only, never touches global elims.
export const useTicketStrikes = (resetKey) => {
  const [struck, setStruck] = React.useState(() => new Set());
  React.useEffect(() => setStruck(new Set()), [resetKey]);
  const isStruck = (slot, n) => struck.has(`${slot}:${n}`);
  const toggle = (slot, n) =>
    setStruck((prev) => {
      const next = new Set(prev);
      const k = `${slot}:${n}`;
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const keep = (slot, posts) => posts.filter((n) => !isStruck(slot, n));
  const countIn = (slot, posts) => posts.filter((n) => isStruck(slot, n)).length;
  return { isStruck, toggle, keep, countIn, clear: () => setStruck(new Set()) };
};

export const StrikeChip = ({ n, testId, struck, onToggle, badge = null }) => {
  const Tag = onToggle ? "button" : "div";
  return (
    <Tag
      type={onToggle ? "button" : undefined}
      data-testid={testId}
      data-struck={struck ? "true" : "false"}
      onClick={onToggle}
      aria-pressed={onToggle ? struck : undefined}
      title={onToggle ? (struck ? `Put #${n} back on this ticket` : `Remove #${n} from this ticket`) : undefined}
      className={`relative rounded-md transition-[opacity,transform] duration-150 ${
        onToggle
          ? "cursor-pointer hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0a0a0a]"
          : ""
      } ${struck ? "opacity-30 grayscale" : ""}`}
    >
      <SaddleCloth n={n} />
      {struck && (
        <span
          className="absolute inset-0 flex items-center justify-center text-[#dc2626] pointer-events-none"
          aria-hidden="true"
        >
          <X size={32} strokeWidth={3.5} />
        </span>
      )}
      {badge}
    </Tag>
  );
};

export const StrikeHint = ({ count, onClear, testId }) => (
  <div
    className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.18em] text-stone-500 pb-1"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
  >
    <span>Tap a horse to take it off this ticket</span>
    {count > 0 && (
      <button
        type="button"
        onClick={onClear}
        data-testid={testId}
        className="inline-flex items-center gap-1 font-bold text-[#f87171] hover:text-[#fecaca] transition-colors"
      >
        <RotateCcw size={11} /> Restore {count}
      </button>
    )}
  </div>
);
